import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ShipmentDetailsCell } from "@freightsimple/ui-core/Cells/ShipmentDetailsCell";
import { StackedTextCell } from "@freightsimple/ui-core/Cells/StackedTextCell";
import { StatusDotCell } from "@freightsimple/ui-core/Cells/StatusDotCell";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import { type ShipmentSummary } from "@freightsimple/generated-carriers-openapi-client/models/ShipmentSummary";
import {
  describeIssueType,
  describeShipmentState,
  formatMoney,
  issueBadgeVariant,
  shipmentStateDotColor,
} from "@/App/Views/carrierFormatting";
import { shipmentHref } from "@/App/carrierRoutes";

// The carrier's booked shipments, in a standard titled panel. PRO number leads
// (the carrier's primary key); the row opens the full shipment detail.
export function ShipmentsTable({
  shipments,
}: {
  shipments: ShipmentSummary[];
}) {
  const columns: Column<ShipmentSummary>[] = [
    {
      key: "pro",
      header: "Pro #",
      render: (shipment) => (
        <StackedTextCell
          primary={shipment.proNumber ?? "—"}
          secondary={shipment.billOfLadingNumber ?? undefined}
          primaryMono
        />
      ),
    },
    {
      // The same cell Hubble uses, so a carrier and an operator looking at the
      // same shipment see the same thing.
      key: "details",
      header: "Details",
      render: (shipment) => (
        <ShipmentDetailsCell
          pickupLabel={shipment.pickup}
          pickupName={shipment.pickupName}
          pickupCountry={shipment.pickupCountry}
          deliveryLabel={shipment.delivery}
          deliveryName={shipment.deliveryName}
          deliveryCountry={shipment.deliveryCountry}
          commodity={shipment.commodity ?? "Unknown"}
          summary={shipment.freightSummary ?? ""}
        />
      ),
    },
    {
      key: "pickup",
      header: "Pickup",
      render: (shipment) => <TextCell value={shipment.pickupDate} />,
    },
    {
      key: "delivered",
      header: "Delivered",
      render: (shipment) => <TextCell value={shipment.deliveryDate} />,
    },
    {
      key: "status",
      header: "Status",
      // The open issue sits under the state rather than in its own column: it is
      // set on a small minority of rows, so a column of its own would be mostly
      // empty, and it qualifies the state rather than standing apart from it.
      render: (shipment) => (
        <div className="flex flex-col items-start gap-1">
          <StatusDotCell
            color={shipmentStateDotColor(shipment.state)}
            label={describeShipmentState(shipment.state)}
          />
          {shipment.openIssueType !== undefined && (
            <Badge
              label={describeIssueType(shipment.openIssueType)}
              variant={issueBadgeVariant(shipment.openIssueType)}
              size="small"
            />
          )}
        </div>
      ),
    },
    {
      // "Cost", not "Paid": until the carrier invoices us this is still the
      // agreed rate, and nothing has been paid.
      key: "paid",
      header: "Cost",
      align: "right",
      render: (shipment) => (
        <TextCell
          value={
            shipment.paidCad === undefined
              ? undefined
              : formatMoney(shipment.paidCad)
          }
        />
      ),
    },
  ];

  return (
    // No Panel: the page header already says "Shipments", and the row count is
    // already on the pager below, so a titled card here was a second header
    // repeating both.
    <Table
      columns={columns}
      data={shipments}
      rowKey={(shipment) => shipment.shipmentId}
      emptyMessage="You don't have any booked shipments yet."
      rowActions={(shipment) => (
        <Button href={shipmentHref(shipment.shipmentId)}>View</Button>
      )}
    />
  );
}
